// The caller's EFFECTIVE permissions as the server computes them: Cognito groups plus
// the permissions granted to the account by name (Backend config/permission_grants.json).
// The group map in FileContext cannot know the per-account grants, so features opened
// to named accounts ask here. A UI hint only — every endpoint re-checks on the server.
import React, { useEffect, useState } from 'react';
import { authHeaders } from './authHeader';

let pending = null;

/** Set of permission names, fetched once per page load (an empty set on failure). */
export function loadServerPermissions() {
  if (!pending) {
    pending = (async () => {
      const res = await fetch('/api/me/permissions', { headers: await authHeaders() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.success === false) throw new Error(body.error || `permissions failed (${res.status})`);
      return new Set(body.permissions || []);
    })().catch(() => { pending = null; return new Set(); });
  }
  return pending;
}

/** {allowed, loading} for one permission. */
export function useServerPermission(permission) {
  const [state, setState] = useState({ allowed: false, loading: true });
  useEffect(() => {
    let live = true;
    loadServerPermissions().then((perms) => { if (live) setState({ allowed: perms.has(permission), loading: false }); });
    return () => { live = false; };
  }, [permission]);
  return state;
}

/** Renders children when the server grants `permission`, else `fallback` (nothing while it loads). */
export function ServerPermissionGate({ permission, fallback = null, children }) {
  const { allowed, loading } = useServerPermission(permission);
  if (loading) return null;
  return allowed ? children : fallback;
}

export default React.memo(ServerPermissionGate);
